import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';

export type HeroTone = 'dark' | 'light';

interface HeroToneState {
  pathname: string;
  tone: HeroTone;
}

interface HeroToneContextValue extends HeroToneState {
  setHeroTone: (tone: HeroTone) => void;
}

const HeroToneContext = createContext<HeroToneContextValue>({
  pathname: '',
  tone: 'dark',
  setHeroTone: () => {},
});

/**
 * Shares the tone (dark / light) of the current page's top hero area with the
 * navbar, so the navbar can pick a readable text color. The tone is stored
 * together with the pathname it belongs to; consumers only trust it when the
 * pathname matches the current location, which keeps a light-hero page from
 * leaking its setting onto the next page (no reset ordering issues).
 */
export function HeroToneProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const pathnameRef = useRef(location.pathname);
  pathnameRef.current = location.pathname;

  const [state, setState] = useState<HeroToneState>({
    pathname: location.pathname,
    tone: 'dark',
  });

  const setHeroTone = useCallback((tone: HeroTone) => {
    setState((prev) =>
      prev.pathname === pathnameRef.current && prev.tone === tone
        ? prev
        : { pathname: pathnameRef.current, tone },
    );
  }, []);

  const value = useMemo(
    () => ({ pathname: state.pathname, tone: state.tone, setHeroTone }),
    [state.pathname, state.tone, setHeroTone],
  );

  return <HeroToneContext.Provider value={value}>{children}</HeroToneContext.Provider>;
}

export function useHeroTone() {
  return useContext(HeroToneContext);
}